import { useCallback, useMemo, useState } from "react"
import { ErrorSchema, FieldProps, utils } from "@rjsf/core"
import { JSONSchema7 } from "json-schema"
import { resolveSchema } from "resha"

import { randID } from "../../../../../../Helpers/Functions/random"
import { useBoolean } from "../../../../../../Hooks/useBoolean"
import { SchemaFormModal } from "../../../Modals"
import { MultiSchemaField } from "../../../Fields"
import { TableHeader } from "../../../Fields/TableHeadersField"
import { tableHeaderUISchema } from "./uiSchema"

export enum TableHeaderModalType {
    New = "new",
    Edit = "edit"
}

type TableHeaderModalProps = {
    onClose: () => void
    onSubmit: (tableHeader: TableHeader) => void
    schema: JSONSchema7
    rootSchema: JSONSchema7
    value?: TableHeader
    fieldProps: FieldProps
    type?: TableHeaderModalType
    externalIds: string[]
}

export const TableHeaderModal = ({
    onClose,
    onSubmit,
    schema,
    rootSchema,
    value,
    type,
    fieldProps,
    externalIds
}: TableHeaderModalProps) => {
    const [state, setState] = useState<TableHeader>(() => {
        if (value) {
            return value
        }

        const optionSchemas = (
            resolveSchema(schema, rootSchema).properties?.options as JSONSchema7
        )?.oneOf as JSONSchema7[]
        const matchingOption = utils.getMatchingOption(
            { type: "String" },
            optionSchemas,
            rootSchema
        )
        const defaultOptions = utils.getDefaultFormState(
            optionSchemas[matchingOption],
            {} as TableHeader["options"],
            rootSchema
        )

        const defaults = utils.getDefaultFormState(
            schema,
            {} as TableHeader,
            rootSchema
        )

        return {
            ...defaults,
            options: defaultOptions as TableHeader["options"]
        } as TableHeader
    })

    const [generateId, , stopGenerateId] = useBoolean(value === undefined)
    const [errorSchema, setErrorSchema] = useState<ErrorSchema>({})
    const handleSubmit = useCallback(() => {
        if (externalIds.includes(state.id)) {
            setErrorSchema({
                id: { __errors: ["Please enter unique ID"] as any }
            })
            return
        }
        const form = fieldProps.formContext.ref.current
        const validationResult = form?.validate(state, {
            definitions: rootSchema.definitions,
            ...schema
        })
        if (!validationResult?.errors.length) {
            onSubmit(state)
            onClose()
        } else {
            setErrorSchema(validationResult.errorSchema)
        }
    }, [state, onSubmit, onClose, fieldProps, schema, rootSchema, externalIds])

    const randomSuffix = useMemo(() => randID(), [])

    const handleChange = (value: TableHeader) => {
        let genId = generateId
        if (generateId && value.id !== state.id) {
            stopGenerateId()
            genId = false
        }
        setState(
            type === TableHeaderModalType.New && genId
                ? {
                      ...value,
                      id: `${
                          (value.title ?? "").replace(/\s/g, "_") || ""
                      }_${randomSuffix}`
                  }
                : value
        )
    }

    const SchemaField = fieldProps.registry.fields.SchemaField
    return (
        <SchemaFormModal
            title={
                type === TableHeaderModalType.Edit
                    ? "Edit Table Header"
                    : "Add new Table Header"
            }
            onClose={onClose}
            onSubmit={handleSubmit}
            submit={{
                label:
                    type === TableHeaderModalType.Edit
                        ? "Update header"
                        : "Add header"
            }}
        >
            <SchemaField
                formData={state}
                onChange={handleChange}
                idSchema={fieldProps.idSchema}
                registry={{
                    ...fieldProps.registry,
                    fields: {
                        ...fieldProps.registry.fields,
                        // Override OneOfField to get options for custom field. Defining in uiSchema doesn't work properly
                        OneOfField: MultiSchemaField
                    }
                }}
                formContext={fieldProps.formContext}
                errorSchema={errorSchema}
                onBlur={() => {}}
                onFocus={() => {}}
                autofocus={false}
                disabled={false}
                readonly={false}
                required={true}
                name=""
                schema={{
                    definitions: rootSchema.definitions,
                    ...schema
                }}
                uiSchema={tableHeaderUISchema}
            />
        </SchemaFormModal>
    )
}
